import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Settings as SettingsIcon, ShieldAlert } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [vat, setVat] = useState("20");
  const [inc, setInc] = useState("25");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/tax").then((r) => {
      setVat(String(Math.round((r.data.vat_rate || 0.2) * 10000) / 100));
      setInc(String(Math.round((r.data.income_tax_rate || 0.25) * 10000) / 100));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const v = parseFloat(vat), i = parseFloat(inc);
    if (isNaN(v) || v < 0 || v > 100) return toast.error("VAT must be between 0 and 100");
    if (isNaN(i) || i < 0 || i > 100) return toast.error("Income tax must be between 0 and 100");
    setSaving(true);
    try {
      await api.put("/settings/tax", { vat_rate: v / 100, income_tax_rate: i / 100 });
      toast.success("Tax rates updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Settings" subtitle="Tax rates · System" testid="settings-title" />
      <PageBody>
        <div className="max-w-xl bg-white border border-slate-300 p-6">
          <div className="flex items-center gap-2 mb-4">
            <SettingsIcon className="w-5 h-5 text-slate-500" />
            <h2 className="font-display text-xl tracking-tight">Turkish tax rates</h2>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            These rates are used by the Accounting page for TL calculations. KDV is applied
            to both purchase and sale invoices; income tax is a percentage of the TL margin (sale − purchase).
          </p>

          {!isAdmin && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Only admins can change these rates.
            </div>
          )}

          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="label-caps">KDV rate (%)</Label>
              <Input
                type="number" step="0.01" min="0" max="100"
                className="rounded-none mt-1 font-mono"
                value={vat}
                disabled={!isAdmin || loading}
                onChange={(e) => setVat(e.target.value)}
                data-testid="settings-vat-rate"
              />
              <div className="text-xs text-slate-400 mt-1">Default 20 (= 20%)</div>
            </div>
            <div>
              <Label className="label-caps">Income tax rate (%) — Gelir vergisi</Label>
              <Input
                type="number" step="0.01" min="0" max="100"
                className="rounded-none mt-1 font-mono"
                value={inc}
                disabled={!isAdmin || loading}
                onChange={(e) => setInc(e.target.value)}
                data-testid="settings-income-tax-rate"
              />
              <div className="text-xs text-slate-400 mt-1">Default 25 (= 25% of TL margin)</div>
            </div>
            <div className="pt-2">
              <Button
                type="submit"
                disabled={!isAdmin || saving || loading}
                className="rounded-none bg-blue-600 hover:bg-blue-700"
                data-testid="settings-save-btn"
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </form>
        </div>
      </PageBody>
    </>
  );
}
